<template>
  <div class="sidebar">
    <div class="sidebar__brand">
      <div class="sidebar__mark">点</div>
      <span v-show="!isCollapse" class="sidebar__name">点墨资产</span>
    </div>

    <el-menu
      :default-active="activeMenu"
      class="sidebar__menu"
      :collapse="isCollapse"
      :collapse-transition="false"
      router
    >
      <div v-show="!isCollapse" class="nav-group-title">驾驶舱</div>
      <el-menu-item index="/dashboard">
        <el-icon><HomeFilled /></el-icon>
        <template #title><span>工作台</span></template>
      </el-menu-item>
      <el-menu-item index="/investor/dashboard">
        <el-icon><DataLine /></el-icon>
        <template #title><span>投资人驾驶舱</span></template>
      </el-menu-item>

      <template v-for="menu in permissionMenus" :key="menu.id">
        <!-- 有子菜单：分组标题 + 平铺子项 -->
        <template v-if="menu.children && menu.children.length > 0">
          <div v-show="!isCollapse" class="nav-group-title">{{ menu.permissionName }}</div>
          <el-menu-item
            v-for="subMenu in menu.children"
            :key="subMenu.id"
            :index="subMenu.path"
          >
            <el-icon><component :is="iconOf(subMenu)" /></el-icon>
            <template #title>
              <span class="nav-label">{{ subMenu.permissionName }}</span>
              <span v-if="badgeOf(subMenu)" class="nav-badge" :class="badgeOf(subMenu).type">
                {{ badgeOf(subMenu).text }}
              </span>
            </template>
          </el-menu-item>
        </template>

        <!-- 无子菜单：直接渲染 -->
        <el-menu-item v-else-if="menu.path" :index="menu.path">
          <el-icon><component :is="iconOf(menu)" /></el-icon>
          <template #title>
            <span class="nav-label">{{ menu.permissionName }}</span>
            <span v-if="badgeOf(menu)" class="nav-badge" :class="badgeOf(menu).type">
              {{ badgeOf(menu).text }}
            </span>
          </template>
        </el-menu-item>
      </template>
    </el-menu>

    <div class="sidebar__foot">
      <button class="collapse-btn" @click="$emit('toggle-collapse')">
        <el-icon><component :is="isCollapse ? 'Expand' : 'Fold'" /></el-icon>
        <span v-show="!isCollapse">收起菜单</span>
      </button>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, onMounted, watch, ref } from 'vue'
import { useRoute } from 'vue-router'
import {
  HomeFilled, Shop, DataAnalysis, TrendCharts, Document, Money, Warning,
  Wallet, List, CircleCheck, CreditCard, Tools, User, UserFilled, Key,
  OfficeBuilding, DataLine, Setting, Menu, Fold, Expand
} from '@element-plus/icons-vue'
import authService from '../../services/authService'
import roleService from '../../services/roleService'
import postInvestService from '../../services/postInvestService'
import store from '../../store'
import { getPermission, setPermission } from '@/utils/auth'

// 按菜单名称关键字推断图标，避免菜单全部落到同一个兜底图标
const ICON_RULES = [
  [/品牌/, 'Shop'],
  [/门店|店铺/, 'Shop'],
  [/估值|评估/, 'DataAnalysis'],
  [/预测/, 'TrendCharts'],
  [/协议|合同/, 'Document'],
  [/分成|资金方|出资/, 'Money'],
  [/风险/, 'Warning'],
  [/回款/, 'Wallet'],
  [/待办/, 'List'],
  [/核销/, 'CircleCheck'],
  [/流水/, 'CreditCard'],
  [/修复/, 'Tools'],
  [/账户|资金/, 'Wallet'],
  [/银行/, 'CreditCard'],
  [/角色/, 'UserFilled'],
  [/用户/, 'User'],
  [/权限/, 'Key'],
  [/租户/, 'OfficeBuilding'],
  [/报表|报告/, 'DataLine'],
  [/设置/, 'Setting']
]

// 后端 icon 字段（el-icon-* 旧格式）映射
const ICON_ALIAS = {
  'el-icon-setting': 'Setting',
  'el-icon-user': 'User',
  'el-icon-lock': 'Key',
  'el-icon-document': 'Document',
  'el-icon-menu': 'Menu',
  'el-icon-grid': 'DataLine',
  'el-icon-shop': 'Shop',
  'el-icon-money': 'Money'
}

const ICON_MAP = {
  HomeFilled, Shop, DataAnalysis, TrendCharts, Document, Money, Warning,
  Wallet, List, CircleCheck, CreditCard, Tools, User, UserFilled, Key,
  OfficeBuilding, DataLine, Setting, Menu, Fold, Expand
}

export default defineComponent({
  name: 'SidebarComponent',
  components: { HomeFilled, DataLine, Fold, Expand },
  props: {
    isCollapse: { type: Boolean, default: false }
  },
  emits: ['toggle-collapse'],
  setup() {
    const route = useRoute()
    const todoCount = ref(0)

    const activeMenu = computed(() => route.path)

    const permissionMenus = computed(() => {
      const permissions = store.state.permissions
      if (!permissions || Object.keys(permissions).length === 0) return []

      const permissionList = Object.values(permissions).filter(
        item => item.permissionType === 1 && item.status === 1
      )
      permissionList.sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))

      const buildMenuTree = (list, parentId = 0) =>
        list
          .filter(item => item.parentId === parentId)
          .map(item => {
            const children = buildMenuTree(list, item.id)
            return { ...item, children: children.length > 0 ? children : null }
          })

      return buildMenuTree(permissionList)
    })

    const iconOf = (menu) => {
      if (menu.icon && ICON_ALIAS[menu.icon]) return ICON_MAP[ICON_ALIAS[menu.icon]]
      const name = menu.permissionName || ''
      for (const [rule, icon] of ICON_RULES) {
        if (rule.test(name)) return ICON_MAP[icon]
      }
      return Menu
    }

    // 待办数量角标：仅对投后待办菜单展示，接口失败时静默降级
    const badgeOf = (menu) => {
      const name = menu.permissionName || ''
      if (/待办/.test(name) && todoCount.value > 0) {
        return { text: todoCount.value > 99 ? '99+' : String(todoCount.value), type: 'is-danger' }
      }
      return null
    }

    const loadTodoCount = async () => {
      try {
        const res = await postInvestService.getTodoList({ tenantId: store.state.tenantId })
        const list = Array.isArray(res?.data) ? res.data : (res?.data?.list || [])
        todoCount.value = list.filter(t => t.status !== 'done' && t.status !== '已完成').length
      } catch {
        todoCount.value = 0
      }
    }

    const loadPermissions = async () => {
      if (!store.state.userInfo?.id) return
      store.state.permissions = null

      const cachedPermission = getPermission(store.state.userInfo.id)
      if (cachedPermission) {
        store.commit('SET_PERMISSIONS', cachedPermission)
      } else {
        try {
          const rolesResponse = await roleService.getRolesByUserId(store.state.userInfo.id)
          const permissionsResponse = await authService.getPermissionsByRoles(rolesResponse.data)
          store.commit('SET_PERMISSIONS', permissionsResponse.data)
          setPermission(permissionsResponse.data, store.state.userInfo.id)
        } catch (error) {
          console.error('获取权限失败:', error)
        }
      }
    }

    onMounted(async () => {
      await loadPermissions()
      loadTodoCount()
    })

    watch(
      () => store.state.userInfo,
      (newInfo, oldInfo) => {
        if (newInfo?.id !== oldInfo?.id) loadPermissions()
      },
      { deep: true }
    )

    return { permissionMenus, activeMenu, iconOf, badgeOf }
  }
})
</script>

<style scoped>
.sidebar {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--bg-card);
  overflow: hidden;
}

.sidebar__brand {
  height: 56px;
  flex: 0 0 56px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 16px;
  border-bottom: 1px solid var(--border);
}

.sidebar__mark {
  width: 26px;
  height: 26px;
  flex: 0 0 26px;
  border-radius: 7px;
  background: var(--brand-500);
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sidebar__name {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
}

.sidebar__menu {
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  border: none;
  padding: 8px;

  /* Element Menu 变量统一到设计 token */
  --el-menu-item-height: 40px;
  --el-menu-bg-color: var(--bg-card);
  --el-menu-text-color: var(--text-secondary);
  --el-menu-active-color: var(--brand-500);
  --el-menu-hover-bg-color: var(--bg-page);
  --el-menu-hover-text-color: var(--text-primary);
  --el-menu-item-font-size: 13px;
  --el-menu-border-color: var(--border);
}

.sidebar__menu:not(.el-menu--collapse) {
  width: 208px;
}

.sidebar__menu.el-menu--collapse {
  width: 48px;
  padding: 8px;
}

.nav-group-title {
  font-size: 11px;
  color: var(--text-tertiary);
  padding: 14px 10px 6px;
  white-space: nowrap;
  overflow: hidden;
}

.nav-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nav-badge {
  margin-left: 8px;
  font-size: 10px;
  line-height: 16px;
  min-width: 16px;
  padding: 0 5px;
  border-radius: 8px;
  text-align: center;
  background: var(--bg-page);
  color: var(--text-secondary);
}

.nav-badge.is-danger {
  background: var(--danger-bg);
  color: var(--danger-text);
}

.sidebar__foot {
  flex: 0 0 auto;
  padding: 8px;
  border-top: 1px solid var(--border);
}

.collapse-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 10px;
  border: none;
  border-radius: var(--radius-control);
  background: transparent;
  color: var(--text-tertiary);
  font-size: 12px;
  font-family: inherit;
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease), color var(--dur-fast) var(--ease);
}

.collapse-btn:hover {
  background: var(--bg-page);
  color: var(--text-secondary);
}

/* 菜单项圆角与激活态 */
.sidebar__menu :deep(.el-menu-item) {
  border-radius: var(--radius-control);
  margin-bottom: 2px;
  color: var(--text-secondary);
  padding: 0 10px;
}

.sidebar__menu :deep(.el-menu-item:hover) {
  background: var(--bg-page);
  color: var(--text-primary);
}

.sidebar__menu :deep(.el-menu-item.is-active) {
  background: var(--bg-selected);
  color: var(--brand-500);
  font-weight: 500;
}

.sidebar__menu :deep(.el-menu-item .el-icon) {
  font-size: 15px;
}

/* 折叠态：图标居中 */
.sidebar__menu.el-menu--collapse :deep(.el-menu-item) {
  justify-content: center;
  padding: 0;
}

.sidebar__menu::-webkit-scrollbar {
  width: 4px;
}
.sidebar__menu::-webkit-scrollbar-thumb {
  background: var(--neutral-200);
  border-radius: 2px;
}
</style>
